import { useCropSelectionClear } from '../use-crop-selection-clear'
import { useCropSession } from '../use-crop-session'
import { useCropState } from '../use-crop-state'
import { useLockFreezeCommands } from '../use-lock-freeze-commands'
import { useNodeEditSession } from '../use-node-edit-session'
import { useNodeEditState } from '../use-node-edit-state'
import { useSelectionHistory } from '../use-selection-history'
import { useTextOnPathSession } from '../use-text-on-path-session'
import { useTextOnPathState } from '../use-text-on-path-state'
import type { CanvasFoundation, CanvasToolModes } from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * The object edit modes — node edit, crop and text on a path — with their enter/exit sessions,
 * plus what those sessions lean on: selection history (including group isolation) and the
 * lock/freeze commands. Also derives whether the canvas currently accepts shape selection.
 */
export function useCanvasEditModes(
  {
    selectedShape,
    isPenToolActive,
    onEnterNodeEditMode,
    onNodeEditModeChange,
    getUndoStackSize,
    onShapeCommitted,
    activeItem,
    onBackgroundClick,
    onShapeSelectionHistory,
  }: ResolvedCenterCanvasConfig,
  {
    internalShapeManagerRef,
    isPanInteractionActive,
    setSelectedGuideIds,
  }: Pick<
    CanvasFoundation,
    'internalShapeManagerRef' | 'isPanInteractionActive' | 'setSelectedGuideIds'
  >,
  {
    isInShapeCreationMode,
    isInPenToolMode,
    isInTextCreationMode,
  }: Pick<CanvasToolModes, 'isInShapeCreationMode' | 'isInPenToolMode' | 'isInTextCreationMode'>
) {
  // Node edit mode state — declared before `canSelectShapes`, which reads `isNodeEditMode`.
  const {
    nodeEditShapeId,
    setNodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeData,
    nodeEditShapeIdRef,
    nodeEditPreDragShapeRef,
    nodeEditOriginalShapeRef,
    nodeEditToolbarRef,
    nodeEditUndoFloorRef,
    isNodeEditMode,
    isPenEditMode,
    penEditHoverKind,
    setPenEditHoverKind,
    exitNodeEditMode,
  } = useNodeEditState({ isPenToolActive, onNodeEditModeChange })

  const {
    cropShapeId,
    setCropShapeId,
    cropShapeData,
    setCropShapeData,
    cropShapeIdRef,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    cropUndoFloorRef,
    isCropMode,
    exitCropMode,
  } = useCropState()

  const {
    textOnPathDraft,
    setTextOnPathDraft,
    textOnPathShapeData,
    setTextOnPathShapeData,
    textOnPathLength,
    setTextOnPathLength,
    textOnPathShapeIdRef,
    textOnPathDraftRef,
    textOnPathToolbarRef,
    isTextOnPathMode,
    exitTextOnPathMode,
    handleTextOnPathTextChange,
    handleTextOnPathStartDistanceChange,
    handleTextOnPathConfigChange,
    handleTextOnPathDotDrag,
    handleTextOnPathLengthChange,
  } = useTextOnPathState()

  const canSelectShapes =
    !isInShapeCreationMode &&
    !isInPenToolMode &&
    !isNodeEditMode &&
    !isCropMode &&
    !isInTextCreationMode &&
    (activeItem === 'select' || activeItem === 'shape' || activeItem === 'type')
  const isCanvasSelectionEnabled = canSelectShapes && !isPanInteractionActive

  const {
    selectedTextBoxIdsRef,
    selectedShapeIdsForUnified,
    setSelectedShapeIdsForUnified,
    groupIsolationPath,
    setGroupIsolationPath,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    enterGroupIsolationMode,
    selectShapesWithHistoryAndTextMeta,
    exitGroupIsolationMode,
    handleShapeSelectionHistoryFromShapeManager,
    activeIsolationGroupId,
  } = useSelectionHistory({
    internalShapeManagerRef,
    setSelectedGuideIds,
    onBackgroundClick,
    onShapeSelectionHistory,
  })

  const lockFreezeCommands = useLockFreezeCommands({
    internalShapeManagerRef,
    activeIsolationGroupId,
  })
  const {
    lockSelectedObjects,
    unlockSelectedObjects,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    toggleFreezeSelectedObjects,
    unlockAllObjects,
    unfreezeAllObjects,
    pulseSelectionGeometryBlocked,
  } = lockFreezeCommands

  const { tryEnterNodeEditMode, isNodeEditShapeGeometryBlocked } = useNodeEditSession({
    internalShapeManagerRef,
    selectedShape,
    isPenToolActive,
    getUndoStackSize,
    nodeEditShapeId,
    setNodeEditShapeId,
    setNodeEditShapeData,
    nodeEditShapeIdRef,
    nodeEditPreDragShapeRef,
    nodeEditOriginalShapeRef,
    nodeEditUndoFloorRef,
    exitNodeEditMode,
    clearShapeSelectionWithHistory,
    pulseSelectionGeometryBlocked,
    onEnterNodeEditMode,
    onNodeEditModeChange,
  })

  const { tryEnterCropMode } = useCropSession({
    internalShapeManagerRef,
    selectedShape,
    getUndoStackSize,
    cropShapeId,
    setCropShapeId,
    setCropShapeData,
    cropShapeIdRef,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    cropUndoFloorRef,
    exitCropMode,
    pulseSelectionGeometryBlocked,
  })

  const {
    commitAndExitTextOnPathMode,
    tryEnterTextOnPathMode,
    resolveTextOnPathTargetId,
    enterTextOnPathModeById,
  } = useTextOnPathSession({
    internalShapeManagerRef,
    selectedShape,
    textOnPathDraftRef,
    textOnPathShapeIdRef,
    setTextOnPathDraft,
    setTextOnPathShapeData,
    setTextOnPathLength,
    exitTextOnPathMode,
    nodeEditShapeIdRef,
    exitNodeEditMode,
    exitCropMode,
    onShapeCommitted,
  })

  useCropSelectionClear({ isCropMode, internalShapeManagerRef })

  return {
    nodeEditShapeId,
    setNodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeData,
    nodeEditShapeIdRef,
    nodeEditPreDragShapeRef,
    nodeEditOriginalShapeRef,
    nodeEditToolbarRef,
    nodeEditUndoFloorRef,
    isNodeEditMode,
    isPenEditMode,
    penEditHoverKind,
    setPenEditHoverKind,
    exitNodeEditMode,
    cropShapeId,
    setCropShapeId,
    cropShapeData,
    setCropShapeData,
    cropShapeIdRef,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    cropUndoFloorRef,
    isCropMode,
    exitCropMode,
    textOnPathDraft,
    textOnPathShapeData,
    textOnPathLength,
    textOnPathDraftRef,
    textOnPathToolbarRef,
    isTextOnPathMode,
    handleTextOnPathTextChange,
    handleTextOnPathStartDistanceChange,
    handleTextOnPathConfigChange,
    handleTextOnPathDotDrag,
    handleTextOnPathLengthChange,
    selectedTextBoxIdsRef,
    selectedShapeIdsForUnified,
    setSelectedShapeIdsForUnified,
    groupIsolationPath,
    setGroupIsolationPath,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    enterGroupIsolationMode,
    selectShapesWithHistoryAndTextMeta,
    exitGroupIsolationMode,
    handleShapeSelectionHistoryFromShapeManager,
    activeIsolationGroupId,
    lockSelectedObjects,
    unlockSelectedObjects,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    toggleFreezeSelectedObjects,
    unlockAllObjects,
    unfreezeAllObjects,
    pulseSelectionGeometryBlocked,
    tryEnterNodeEditMode,
    isNodeEditShapeGeometryBlocked,
    tryEnterCropMode,
    commitAndExitTextOnPathMode,
    tryEnterTextOnPathMode,
    resolveTextOnPathTargetId,
    enterTextOnPathModeById,
    canSelectShapes,
    isCanvasSelectionEnabled,
    lockFreezeCommands,
  }
}
